import React, { Component } from "react";
import { Row, Col, Card, Button, Container, Form, Accordion, RowToggleDataTable } from "react-bootstrap";
import "./_hostelAttendance.scss"
import SelectDropDown from '../selectdropdown/SelectDropDown';
import { components } from 'react-select';
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import * as Cookies from "es-cookie";
import * as compose from "lodash.flowright";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import MultiSelect from "react-multi-select-component";
import { attachModuleGuard, AccessDeniedModal } from "../../../utils/moduleGuard";
import BuildingCreationModal from "./buildingCreationModal";
import S from "./buildingFinanceStyles";
import { BuildingListShimmer } from "./buildingShimmer";
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";

// /building-creation → "Hostel_Buildings" (see MODULE_ROUTES in moduleAccessGuard).
// module_key ONLY, never module_name: the sidebar group name is shared by sibling
// modules, and can() grants an action when ANY matched row allows it — matching the
// group would let another module's grant leak in.
//
// This section owns the only true server-side delete in the module (deleteBuilding).
const HOSTEL_BUILDINGS_MODULE = ["Hostel_Buildings"];

class BuildingCreationSection extends Component {
    constructor(props) {
        super();
        this.state = {
            branchSearch: "",
            branchError: "",
            buildingCreationModal: false,
            existingBuildingCreationData: [],
            activeBuildingDetails: null,
            activeFloorDetails: null,
            activeRoomDetails: null,
            activeCapacityDetails: null,
            getBuildingData: [],
            getFloorData: [],
            getRoomData: [],
            loading: 0,
            noDateError: "",
            buildingFilter: "",
            accessDeniedShow: false,
        }

        this.guard = attachModuleGuard(this, HOSTEL_BUILDINGS_MODULE);
    }

    // The two entry points into BuildingCreationModal (create + edit). Gating the openers
    // stops the write; the buttons stay visible and clickable. The modal re-checks at its
    // own submit.
    openCreateBuilding = () => {
        if (!this.guard.requireEdit()) return;
        this.setState({ buildingCreationModal: true, existingBuildingCreationData: [] });
    };

    openEditBuilding = (activeBuilding) => {
        if (!this.guard.requireEdit()) return;
        this.setState({ buildingCreationModal: true, existingBuildingCreationData: [activeBuilding] });
    };

    // ── Stat helpers (kept tiny so the JSX stays readable) ──
    countRooms = (building) =>
        building.floors_details.reduce(
            (t, f) => t + (f.rooms_details ? f.rooms_details.length : 0),
            0,
        );

    countCapacity = (building) =>
        building.floors_details.reduce(
            (t, f) =>
                t +
                (f.rooms_details
                    ? f.rooms_details.reduce((c, r) => c + (r.room_capacity || 0), 0)
                    : 0),
            0,
        );

    floorCapacity = (floor) =>
        floor.rooms_details.reduce((c, r) => c + (r.room_capacity || 0), 0);

    // Select a building in the master list → show only its floors in the detail pane.
    selectBuilding = (item) => {
        this.setState({
            activeBuildingDetails: item.hostel_building_id,
            activeFloorDetails: null,
            getFloorData: item.floors_details,
        });
    };

    // Expand / collapse a floor inside the detail pane.
    toggleFloor = (floorId) => {
        this.setState((prev) => ({
            activeFloorDetails:
                prev.activeFloorDetails === floorId ? null : floorId,
        }));
    };

    // Soft-delete a building (and its floors/rooms/allotments) then refresh the list.
    deleteBuilding = async (e, item) => {
        e.stopPropagation(); // don't trigger selectBuilding on the row

        // Checked BEFORE the confirm prompt: a role without is_delete should be told they
        // lack access, not asked to confirm a delete that can't happen.
        if (!this.guard.requireDelete()) return;

        if (!window.confirm(`Delete building "${item.hostel_building_name}"?\nThis will remove its floors, rooms and student allotments.`)) {
            return;
        }
        try {
            await this.props.client.mutate({
                mutation: gql`
                    mutation($hostel_building_id: Int){
                        deleteHostelBuilding(hostel_building_id: $hostel_building_id)
                    }
                `,
                variables: { hostel_building_id: item.hostel_building_id },
            });
            // Clear selection if the deleted building was active, then reload.
            this.setState({
                activeBuildingDetails: null,
                activeFloorDetails: null,
                getFloorData: [],
            });
            this.getBuildingCreationDetails();
        } catch (err) {
            alert("Failed to delete building. " + err.message);
        }
    };
    componentDidMount() {
        this.loadForBranchScope();
        // Branch comes from the header (NavbarOne) — reload when it changes.
        this.offBranchScope = onBranchScopeChange(() => this.loadForBranchScope());
    }

    componentDidUpdate(prevProps) {
        // globalBranches arrives async — load once it lands.
        if (prevProps.globals?.globalBranches !== this.props.globals?.globalBranches) {
            this.loadForBranchScope();
        }
    }

    componentWillUnmount() {
        if (this.offBranchScope) this.offBranchScope();
    }

    // The branches this page covers: the header's selection, narrowed to what the user
    // is authorized for. There is no branch picker here any more.
    scopedBranchIds = () =>
        scopeBranches(this.props.globals?.globalBranches || []).map((b) => parseInt(b.id));

    loadForBranchScope = () => {
        if (this.scopedBranchIds().length === 0) {
            this.setState({ getBuildingData: [], loading: 0 });
            return;
        }
        this.setState({
            branchError: "",
            getBuildingData: [],
            noDateError: "",
            buildingFilter: "",
            activeBuildingDetails: null,
            activeFloorDetails: null,
            getFloorData: [],
            loading: 1,
        }, () => this.getBuildingCreationDetails());
    };

    // getBuildingCreationDetails takes a single branch_id, so fan it out across the
    // header's branch scope and concatenate — each building belongs to one branch.
    getBuildingCreationDetails = async () => {
        const results = await Promise.all(this.scopedBranchIds().map((branch_id) => this.props.client.query({
            query: gql`
                query($institute_id: Int!, $branch_id: Int!){
                    getBuildingCreationDetails(institute_id: $institute_id, branch_id:$branch_id) {
                        hostel_building_id
                        hostel_building_name
                        floors_details{
                          hostel_building_floor_id:floor_id
                          floor_name
                          floor_incharge_id
                          rooms_details{
                            room_id
                            room_name
                            room_capacity
                            capacity_details{
                              edu_student_id
                              attendance_status
                              attended_date
                            }
                          }
                        }
                    }
                }
                `,
            variables: {
                institute_id: parseInt(Cookies.get("institutionid")),
                branch_id: branch_id
            },
            fetchPolicy: 'network-only'
        })))

        const buildings = results.flatMap((r) => (r.data && r.data.getBuildingCreationDetails) || [])
        if (buildings.length > 0) {
            let filter_data = buildings
            // Keep the building the user was viewing selected (e.g. after an
            // in-place save/refresh); otherwise default to the latest one.
            const stillSelected = filter_data.find(
                (b) => b.hostel_building_id === this.state.activeBuildingDetails);
            const defaultBuilding = stillSelected || [...filter_data].sort(
                (a, b) => b.hostel_building_id - a.hostel_building_id)[0];
            this.setState({
                getBuildingData: filter_data,
                loading: 0,
                activeBuildingDetails: defaultBuilding.hostel_building_id,
                activeFloorDetails: null,
                getFloorData: defaultBuilding.floors_details,
            })

        } else {
            this.setState({ loading: 0, getBuildingData: [], noDateError: "No Data Available" })

        }
    }

    handleAccordionClick1 = (item, name) => {

        console.log("item", item);

        if (name == "building") {
            if (this.state.activeBuildingDetails == item.hostel_building_id) {
                this.setState({ activeBuildingDetails: null, activeFloorDetails: null, activeRoomDetails: null, activeCapacityDetails: null, getFloorData: [], getRoomData: [], getStudentData: [] });
            } else if (this.state.activeBuildingDetails != null && this.state.activeBuildingDetails != item.hostel_building_id) {
                const floorData = this.state.getBuildingData.filter((a) => a.hostel_building_id === item.hostel_building_id)[0].floors_details;
                this.setState({ activeBuildingDetails: item.hostel_building_id, activeFloorDetails: null, activeRoomDetails: null, activeCapacityDetails: null, getFloorData: floorData, getRoomData: [], getStudentData: [] });
            } else {
                const floorData = this.state.getBuildingData.filter((a) => a.hostel_building_id === item.hostel_building_id)[0].floors_details;
                console.log("floorData", floorData);
                this.setState({ activeBuildingDetails: item.hostel_building_id, getFloorData: floorData });
            }
        }


        if (name == "floor") {

            if (this.state.activeFloorDetails === item) {
                this.setState({ activeFloorDetails: null, activeRoomDetails: null, activeCapacityDetails: null, getRoomData: [] });
            } else {

                const roomData = this.state.getFloorData.filter((a) => a.hostel_building_floor_id === item)[0].rooms_details;
                this.setState({ activeFloorDetails: item, getRoomData: roomData });
            }
        }

    };

    handleBranchSubmit = (e) => {
        e.preventDefault();
        this.loadForBranchScope();
    }


    render() {
        console.log("maheshparent1", this.state);
        console.log("maheshparent2", this.props);

        if (this.props.studentCreationModal) {
            this.state.buildingCreationModal = true
        }
        return (
            <div style={S.page}>
                {/* ── Building Creation Card ── */}
                <div style={S.card}>
                    <div style={S.cardHeader}>
                        <div style={S.iconWrapperCreate}>
                            <i className="fal fa-building" />
                        </div>
                        <div style={S.cardTitleText}>
                            <h5 style={S.cardTitle}>Building Creation</h5>
                            <span style={S.cardSubtitle}>Create hostel buildings, floors and rooms</span>
                        </div>
                        <button
                            style={S.addBtn}
                            onClick={this.openCreateBuilding}
                        >
                            <i className="fal fa-plus" /> New Building
                        </button>
                    </div>

                    <div style={S.cardBody}>
                        <Row className="align-items-end">
                            {/* No Branch picker — the header (NavbarOne) scopes this page. */}
                        </Row>

                        {
                            this.state.getBuildingData.length > 0 ? (
                                (() => {
                                    const q = this.state.buildingFilter.trim().toLowerCase();
                                    // Newest buildings first (auto-incremented id → higher = more recent).
                                    const sortedBuildings = [...this.state.getBuildingData].sort(
                                        (a, b) => b.hostel_building_id - a.hostel_building_id);
                                    const filteredBuildings = q
                                        ? sortedBuildings.filter((b) =>
                                            (b.hostel_building_name || "").toLowerCase().includes(q))
                                        : sortedBuildings;
                                    const activeBuilding = this.state.getBuildingData.find(
                                        (b) => b.hostel_building_id === this.state.activeBuildingDetails);

                                    return (
                                        <div style={S.mdWrap}>
                                            {/* ── Master list: searchable buildings ── */}
                                            <div style={S.listPane}>
                                                <div style={S.searchBox}>
                                                    <i className="fal fa-search" style={S.searchIcon} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search building…"
                                                        value={this.state.buildingFilter}
                                                        onChange={(e) => this.setState({ buildingFilter: e.target.value })}
                                                        style={S.searchInput}
                                                    />
                                                </div>

                                                <div style={S.listScroll}>
                                                    {filteredBuildings.length > 0 ? (
                                                        filteredBuildings.map((item) => {
                                                            const isActive = this.state.activeBuildingDetails === item.hostel_building_id;
                                                            return (
                                                                <div
                                                                    key={item.hostel_building_id}
                                                                    onClick={() => this.selectBuilding(item)}
                                                                    style={isActive ? S.buildingItemActive : S.buildingItem}
                                                                >
                                                                    <div style={S.buildingItemName}>
                                                                        <i className="fal fa-building" style={{ color: "#4f46e5" }} />
                                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.hostel_building_name}</span>
                                                                        <button
                                                                            type="button"
                                                                            title="Delete building"
                                                                            onClick={(e) => this.deleteBuilding(e, item)}
                                                                            style={{ marginLeft: "auto", background: "transparent", border: "none", color: "#ef4444", cursor: "pointer", padding: 4, lineHeight: 1, flexShrink: 0 }}
                                                                        >
                                                                            <i className="far fa-trash-alt" />
                                                                        </button>
                                                                    </div>
                                                                    <div style={S.miniChipRow}>
                                                                        <span style={S.miniChip}>Floors {item.floors_details.length}</span>
                                                                        <span style={S.miniChip}>Rooms {this.countRooms(item)}</span>
                                                                        <span style={S.miniChip}>Cap {this.countCapacity(item)}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div style={{ ...S.noData, padding: "32px 0" }}>
                                                            <i className="fal fa-search" style={{ fontSize: 26, marginBottom: 8 }} />
                                                            <span style={{ fontSize: 13 }}>No building matches “{this.state.buildingFilter}”</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* ── Detail pane: floors & rooms of the selected building ── */}
                                            <div style={S.detailPane}>
                                                {activeBuilding ? (
                                                    <>
                                                        <div style={S.detailHeader}>
                                                            <h5 style={S.detailTitle}>
                                                                <i className="fal fa-building" style={{ color: "#4f46e5" }} />
                                                                {activeBuilding.hostel_building_name}
                                                            </h5>
                                                            <span style={S.statChip}>Floors : {activeBuilding.floors_details.length}</span>
                                                            <span style={S.statChip}>Rooms : {this.countRooms(activeBuilding)}</span>
                                                            <span style={S.statChip}>Capacity : {this.countCapacity(activeBuilding)}</span>
                                                            <button
                                                                style={{ ...S.addBtn, padding: "7px 16px", fontSize: 13 }}
                                                                onClick={() => this.openEditBuilding(activeBuilding)}
                                                            >
                                                                <i className="far fa-edit" /> Edit
                                                            </button>
                                                        </div>

                                                        {activeBuilding.floors_details.length > 0 ? (
                                                            activeBuilding.floors_details.map((floor) => {
                                                                const isOpen = this.state.activeFloorDetails === floor.hostel_building_floor_id;
                                                                return (
                                                                    <div key={floor.hostel_building_floor_id} style={S.floorBlock}>
                                                                        <div
                                                                            style={S.floorHead}
                                                                            onClick={() => this.toggleFloor(floor.hostel_building_floor_id)}
                                                                        >
                                                                            <i className={`fa-fw far ${isOpen ? "fa-minus-square" : "fa-plus-square"}`} style={{ color: "#6366f1" }} />
                                                                            <span style={S.floorName}>
                                                                                <i className="fal fa-layer-group" />{floor.floor_name}
                                                                            </span>
                                                                            <span style={{ ...S.statChip, marginLeft: "auto" }}>Rooms : {floor.rooms_details.length}</span>
                                                                            <span style={S.statChip}>Capacity : {this.floorCapacity(floor)}</span>
                                                                        </div>

                                                                        {isOpen && (
                                                                            floor.rooms_details.length > 0 ? (
                                                                                <div style={S.roomGrid}>
                                                                                    {floor.rooms_details.map((room) => (
                                                                                        <div key={room.room_id} style={S.roomCard}>
                                                                                            <div style={S.roomName}>
                                                                                                <i className="fal fa-door-open" style={{ color: "#7c3aed" }} />
                                                                                                {room.room_name}
                                                                                            </div>
                                                                                            <div style={S.roomCap}>Capacity : {room.room_capacity}</div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <div style={{ padding: "14px 16px", color: "#94a3b8", fontSize: 13 }}>
                                                                                    No rooms in this floor.
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <div style={{ padding: "20px 0", color: "#94a3b8", fontSize: 13, textAlign: "center" }}>
                                                                No floors configured for this building.
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div style={S.detailPlaceholder}>
                                                        <i className="fal fa-hand-pointer" style={{ fontSize: 34, marginBottom: 12 }} />
                                                        <h6 style={{ color: "#64748b", margin: 0 }}>Select a building</h6>
                                                        <span style={{ fontSize: 13, marginTop: 4 }}>Pick a building from the left to view its floors and rooms.</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()
                            ) : (
                                <div className="mt-3">
                                    {this.state.loading == 1 ? (
                                        <BuildingListShimmer rows={3} />
                                    ) : this.state.noDateError != "" ? (
                                        <div style={S.noData}>
                                            <i className="fal fa-building" style={{ fontSize: 40, marginBottom: 12 }} />
                                            <h6 style={{ color: "#64748b", margin: 0 }}>{this.state.noDateError}</h6>
                                            <span style={{ fontSize: 13, marginTop: 4 }}>Select a branch and submit to view buildings</span>
                                        </div>
                                    ) : ("")}
                                </div>
                            )}
                    </div>
                </div>

                <BuildingCreationModal
                    stateData={this.state}
                    show={this.state.buildingCreationModal}
                    onHide={() => this.setState({ buildingCreationModal: false })}
                    onSaved={(branchId) => {
                        // Show the shimmer while the list refreshes in place after
                        // an add/edit (clearing the data lets the shimmer branch render).
                        this.setState({
                            loading: 1,
                            getBuildingData: [],
                            activeBuildingDetails: null,
                            activeFloorDetails: null,
                            getFloorData: [],
                            noDateError: "",
                        }, () => this.getBuildingCreationDetails());
                    }}
                    globals={this.props.globals}
                />

                {/* Shown instead of creating / editing / deleting a building when the
                    grant is missing. One modal serves every intercepted action. */}
                <AccessDeniedModal
                    show={this.state.accessDeniedShow}
                    onHide={this.guard.hideAccessDenied}
                />
            </div>
        )
    }
}
export default withRouter(withApollo((BuildingCreationSection)));
